import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ImagePlus, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RichTextEditor } from '../components/blog/RichTextEditor';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../lib/utils';
import { BLOG_CATEGORIES, BLOG_STATUS_STYLES } from '../constants/blog';
import { DOCTOR_SPECIALIZATIONS } from '../constants/specializations';

const EMPTY = { title: '', excerpt: '', content: '', cover_image: '', category: '', tags: [], related_specialization: '', seo_title: '', seo_description: '' };

const uploadImage = async (file) => {
  const fd = new FormData();
  fd.append('file', file);
  const res = await apiRequest('/my/blogs/upload-image', { method: 'POST', body: fd });
  return res.file_url;
};

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-sm font-medium text-slate-800 mb-1.5">{label}</label>
    {children}
    {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
  </div>
);

const CoverPicker = ({ value, onChange }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setBusy(true);
    try { onChange(await uploadImage(f)); } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <div>
      {value ? (
        <div className="relative rounded-xl overflow-hidden">
          <img src={value} alt="Cover" className="w-full h-48 object-cover" data-testid="blog-cover-preview" />
          <button type="button" onClick={() => onChange('')} className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5" data-testid="blog-cover-remove"><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} className="w-full h-40 border-2 border-dashed border-slate-200 rounded-xl flex flex-col items-center justify-center text-slate-500 hover:border-[#3B1C73] hover:text-[#3B1C73] transition-colors" data-testid="blog-cover-upload-btn">
          {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <ImagePlus className="w-6 h-6" />}
          <span className="text-sm mt-2">{busy ? 'Uploading…' : 'Upload cover photo (max 5 MB)'}</span>
        </button>
      )}
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="blog-cover-input" />
    </div>
  );
};

const TagInput = ({ tags, onChange }) => {
  const [text, setText] = useState('');
  const add = () => {
    const t = text.trim().replace(/,$/, '');
    if (t && !tags.includes(t) && tags.length < 10) onChange([...tags, t]);
    setText('');
  };
  return (
    <div className="flex flex-wrap items-center gap-2 border border-slate-200 rounded-md px-2 py-1.5 bg-white">
      {tags.map((t) => (
        <span key={t} className="inline-flex items-center gap-1 bg-purple-50 text-[#3B1C73] text-xs px-2 py-1 rounded-full">
          {t}<button type="button" onClick={() => onChange(tags.filter((x) => x !== t))}><X className="w-3 h-3" /></button>
        </span>
      ))}
      <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } }} onBlur={add}
        placeholder={tags.length ? '' : 'Type a tag and press Enter'} className="flex-1 min-w-[140px] text-sm outline-none py-1" data-testid="blog-tags-input" />
    </div>
  );
};

export default function BlogEditor() {
  const { blogId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(!!blogId);
  const [saving, setSaving] = useState(null);
  const isAdmin = user?.role === 'admin';
  const backTo = isAdmin ? '/physign-admin-panel' : '/provider';
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  useEffect(() => {
    if (!blogId) return;
    apiRequest(`/my/blogs/${blogId}`)
      .then((b) => { setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, b[k] ?? EMPTY[k]])) }); setStatus(b.status); })
      .catch((e) => { toast.error(e.message); navigate(backTo); })
      .finally(() => setLoading(false));
  }, [blogId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (action) => {
    if (!form.title.trim()) return toast.error('Please add a title');
    if (action === 'publish' && !form.content) return toast.error('Please write some content before publishing');
    setSaving(action);
    try {
      const body = JSON.stringify({ ...form, related_specialization: form.related_specialization.trim() || null, action });
      const saved = await apiRequest(blogId ? `/my/blogs/${blogId}` : '/my/blogs', { method: blogId ? 'PUT' : 'POST', body });
      toast.success(saved.status === 'published' ? 'Blog published!' : saved.status === 'pending' ? 'Sent to admin for approval' : 'Draft saved');
      navigate(backTo, { state: { tab: 'blogs' } });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="blog-editor-page">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3">
          <Link to={backTo} state={{ tab: 'blogs' }} className="inline-flex items-center gap-2 text-slate-600 hover:text-[#3B1C73] text-sm" data-testid="blog-editor-back">
            <ArrowLeft className="w-4 h-4" />Back to dashboard
          </Link>
          <div className="flex items-center gap-2">
            {status && <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${BLOG_STATUS_STYLES[status]?.className}`}>{BLOG_STATUS_STYLES[status]?.label}</span>}
            <Button variant="outline" onClick={() => save('draft')} disabled={!!saving} data-testid="blog-save-draft-btn">
              {saving === 'draft' && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Save draft
            </Button>
            <Button className="bg-[#3B1C73] hover:bg-[#2c1457] text-white" onClick={() => save('publish')} disabled={!!saving} data-testid="blog-publish-btn">
              {saving === 'publish' && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{isAdmin ? 'Publish' : 'Submit for approval'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-4 min-w-0">
          <input value={form.title} onChange={set('title')} placeholder="Blog title" maxLength={200}
            className="w-full text-2xl sm:text-3xl font-bold bg-transparent outline-none placeholder:text-slate-300" data-testid="blog-title-input" />
          <Textarea value={form.excerpt} onChange={set('excerpt')} rows={2} maxLength={300} placeholder="Short summary (shown on the blog list)" data-testid="blog-excerpt-input" />
          <RichTextEditor value={form.content} onChange={set('content')} onUploadImage={uploadImage} />
        </div>

        <aside className="space-y-5 bg-white border border-slate-100 rounded-2xl p-5 h-fit">
          <Field label="Cover photo"><CoverPicker value={form.cover_image} onChange={set('cover_image')} /></Field>
          <Field label="Category">
            <Select value={form.category || undefined} onValueChange={set('category')}>
              <SelectTrigger data-testid="blog-category-select"><SelectValue placeholder="Choose a category" /></SelectTrigger>
              <SelectContent className="bg-white">{BLOG_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Tags" hint="Up to 10 tags"><TagInput tags={form.tags} onChange={set('tags')} /></Field>
          <Field label="Show related doctors" hint="Doctors of this speciality appear below the blog">
            <Select value={form.related_specialization || undefined} onValueChange={set('related_specialization')}>
              <SelectTrigger data-testid="blog-related-spec-select"><SelectValue placeholder="Any speciality" /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">{DOCTOR_SPECIALIZATIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="pt-4 border-t border-slate-100 space-y-4">
            <p className="text-sm font-semibold text-slate-900">Google search settings</p>
            <Field label="SEO title" hint={`${form.seo_title.length}/60 · leave empty to use blog title`}>
              <Input value={form.seo_title} onChange={set('seo_title')} maxLength={70} data-testid="blog-seo-title-input" />
            </Field>
            <Field label="SEO description" hint={`${form.seo_description.length}/160 · leave empty to use summary`}>
              <Textarea value={form.seo_description} onChange={set('seo_description')} rows={3} maxLength={170} data-testid="blog-seo-desc-input" />
            </Field>
          </div>
        </aside>
      </main>
    </div>
  );
}
